import { CommonServerPreferences } from '../../auth/types/preferences.type';
import { Annotator } from '../../ecg/types/annotation-tasks.type';
import { ServerPTags, ServerQrsTags } from '../../wave/types/tag.type';
import { ServerWaveType } from '../../wave/types/wave.type';
import { secondsToTime } from '../utils/task.utils';
import { FieldValues } from 'react-hook-form';

export interface FilterQuery {
  status: string;
  startDate: string;
  endDate: string;
  searchInput: string;
  selectedAnnotators: string[];
  selectedGrids: string[];
  selectedEfforts: string[];
}

export enum EPlotType {
  DailyUserCompletion = 'dailyUserCompletion',
  DailyTotalCompletion = 'dailyTotalCompletion',
  DailyUserWorktime = 'dailyUserWorktime',
}

export const annotationsPerUserOptions: ApexCharts.ApexOptions = {
  chart: {
    toolbar: {
      show: false,
    },
    zoom: {
      enabled: false,
    },
  },
  dataLabels: {
    enabled: false,
  },
  plotOptions: {
    bar: {
      horizontal: false,
      borderRadius: 6,
      borderRadiusApplication: 'end',
    },
  },
  stroke: {
    show: true,
    width: 2,
  },
  legend: {
    show: true,
    position: 'bottom' as const,
    horizontalAlign: 'right' as const,
    itemMargin: { horizontal: 14 },
  },
  tooltip: {
    shared: true,
    intersect: false,
    x: {
      formatter: function (value) {
        return `${new Date(value).toDateString()}`;
      },
    },
    y: {
      formatter: function (value) {
        return `${value ? value : 0} annotations`;
      },
    },
  },
  xaxis: {
    type: 'datetime',
    labels: {
      format: 'dd MMM',
    },
  },
  yaxis: [
    {
      labels: {
        formatter: function (value) {
          return `${value ? value.toFixed(0) : 0}`;
        },
      },
    },
  ],
};

export const annotationsTotalOptions: ApexCharts.ApexOptions = {
  chart: {
    toolbar: {
      show: false,
    },
    zoom: {
      enabled: false,
    },
  },
  markers: {
    size: 5,
    strokeWidth: 2,
    shape: 'circle',
  },
  dataLabels: {
    enabled: false,
  },
  stroke: {
    show: true,
    width: 2,
  },
  legend: {
    show: true,
    position: 'bottom' as const,
    horizontalAlign: 'right' as const,
    itemMargin: { horizontal: 14 },
  },
  tooltip: {
    shared: true,
    intersect: false,
    x: {
      formatter: function (value) {
        return `${new Date(value).toDateString()}`;
      },
    },
    y: {
      formatter: function (value) {
        return `${value ? value : 0} annotations`;
      },
    },
  },
  xaxis: {
    type: 'datetime',
    labels: {
      format: 'dd MMM',
    },
  },
  yaxis: [
    {
      labels: {
        formatter: function (value) {
          return `${value ? value.toFixed(0) : 0}`;
        },
      },
    },
  ],
};

export const hoursChartOptions: ApexCharts.ApexOptions = {
  chart: {
    toolbar: {
      show: false,
    },
    zoom: {
      enabled: false,
    },
  },
  markers: {
    size: 5,
    strokeWidth: 2,
    shape: 'circle',
  },
  dataLabels: {
    enabled: false,
  },
  stroke: {
    show: true,
    width: 2,
  },
  legend: {
    show: true,
    position: 'bottom' as const,
    horizontalAlign: 'right' as const,
    itemMargin: { horizontal: 14 },
  },
  tooltip: {
    shared: true,
    intersect: false,
    x: {
      formatter: function (value) {
        return `${new Date(value).toDateString()}`;
      },
    },
    y: {
      formatter: function (value) {
        return secondsToTime(value);
      },
    },
  },
  yaxis: [
    {
      labels: {
        formatter: function (value) {
          return `${value ? (value / 3600).toFixed(2) : 0} hours`;
        },
      },
    },
  ],
};

export interface Tags {
  [k: string]: boolean;
}

export const waveTypes: ServerWaveType[] = ['P', 'QRS', 'T', 'INV'];
export const PTypes: ServerPTags[] = [
  'sinus',
  'pac',
  'sve',
  'atrial_paced',
  'atrial_escape',
  'retrograde',
];

export const QRSTypes: ServerQrsTags[] = [
  'sinus',
  'pac',
  'pvc',
  'pjc',
  'sve',
  'atrial_fibrillation',
  'atrial_flutter',
  'ventricular_paced',
  'atrial_paced',
  'junctional_escape',
  'ventricular_escape',
  'atrial_escape',
  'noisy_rr',
];

export const settings = [
  'platane_save_diagnostic',
  'platane_save_delineation',
  'platane_auto_import_delineation',
  'platane_show_algo_diagnostic',
  'platane_show_algo_delineation',
  'platane_show_coworker_diagnostic',
  'platane_show_coworker_delineation',
  'platane_show_laddergram',
];

export const tagsPToDisplay = [
  { displayValue: 'sinus', value: 'sinus' },
  { displayValue: 'pac', value: 'pac' },
  { displayValue: 'sve', value: 'sve' },
  { displayValue: 'atrial_paced', value: 'atrial_paced' },
  { displayValue: 'atrial_escape', value: 'atrial_escape' },
  { displayValue: 'retrograde', value: 'retrograde' },
];

export const tagsQRSToDisplay = [
  { displayValue: 'sinus', value: 'sinus' },
  { displayValue: 'pac', value: 'pac' },
  { displayValue: 'pvc', value: 'pvc' },
  { displayValue: 'pjc', value: 'pjc' },
  { displayValue: 'sve', value: 'sve' },
  { displayValue: 'atrial_fibrillation', value: 'atrial_fibrillation' },
  { displayValue: 'atrial_flutter', value: 'atrial_flutter' },
  { displayValue: 'ventricular_paced', value: 'ventricular_paced' },
  { displayValue: 'atrial_paced', value: 'atrial_paced' },
  { displayValue: 'junctional_escape', value: 'junctional_escape' },
  { displayValue: 'atrial_escape', value: 'atrial_escape' },
  { displayValue: 'noisy_rr', value: 'noisy_rr' },
];

export const settingsToDisplay = [
  { displayValue: 'platane_save_diagnostic', value: 'platane_save_diagnostic' },
  {
    displayValue: 'platane_save_delineation',
    value: 'platane_save_delineation',
  },
  {
    displayValue: 'platane_auto_import_delineation',
    value: 'platane_auto_import_delineation',
  },
  {
    displayValue: 'platane_show_algo_diagnostic',
    value: 'platane_show_algo_diagnostic',
  },
  {
    displayValue: 'platane_show_algo_delineation',
    value: 'platane_show_algo_delineation',
  },
  {
    displayValue: 'platane_show_coworker_diagnostic',
    value: 'platane_show_coworker_diagnostic',
  },
  {
    displayValue: 'platane_show_coworker_delineation',
    value: 'platane_show_coworker_delineation',
  },
  { displayValue: 'platane_show_laddergram', value: 'platane_show_laddergram' },
];

export interface FormValues extends FieldValues {
  name: string;
  priority: number | string;
  nb_annotations: number | string;
  dataset_id: string;
  annotators: Annotator[];
  description: string;
  annotation_grid: string;
  honeypot_dataset_id: string;
  honeypot_probability: number | string;
  project_id: string;
  settings: CommonServerPreferences;
}

export const defaultPTags: Record<ServerPTags, boolean> = {
  sinus: true,
  pac: true,
  sve: true,
  atrial_paced: true,
  atrial_escape: true,
  retrograde: true,
};

export const defaultQrsTags: Record<ServerQrsTags, boolean> = {
  sinus: true,
  pac: true,
  pvc: true,
  pjc: true,
  sve: true,
  atrial_fibrillation: true,
  atrial_flutter: true,
  ventricular_paced: true,
  atrial_paced: true,
  junctional_escape: true,
  ventricular_escape: true,
  atrial_escape: true,
  noisy_rr: true,
};
